import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import dayjs from 'dayjs';
import { Settings } from '../../models/RoboCall/Settings';
import { Status } from '../../models/common/Status';
import { RootState } from '../store';
import { apiClientWithApi } from '../../utils/http';

export interface SettingsState {
  settings: Settings;
  status: Status;
}

const initialState: SettingsState = {
  settings: {} as Settings,
  status: {
    responseLoading: true,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

export const getSettings = createAsyncThunk(
  'settings/getSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get<Settings>('settings');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else return rejectWithValue('Серверная ошибка!');
    }
  },
);

export const saveSettings = createAsyncThunk(
  'settings/createSettings',
  async (settings: Settings) => {
    try {
      await apiClientWithApi.post('settings', settings);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.message;
      } else return 'Не удалось сохранить настройки!';
    }
  },
);

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    updateRequestStatus: (state) => {
      state.status.requestSuccess = null;
      state.status.requestError = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getSettings.pending, (state: SettingsState) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
      })
      .addCase(getSettings.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        state.settings = {
          ...action.payload,
          workingTime: [
            dayjs(action.payload.startWorking, 'HH:mm'),
            dayjs(action.payload.endWorking, 'HH:mm'),
          ],
          weekendTime: [
            dayjs(action.payload.startWorkingWeekend, 'HH:mm'),
            dayjs(action.payload.endWorkingWeekend, 'HH:mm'),
          ],
        };
      })
      .addCase(getSettings.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось загрузить настройки';
      })
      .addCase(saveSettings.fulfilled, (state) => {
        state.status.requestSuccess = 'Настройки успешно сохранены';
        state.status.requestError = null;
      })
      .addCase(saveSettings.rejected, (state) => {
        state.status.requestError = 'Не удалось сохранить настройки';
      });
  },
});

export default settingsSlice.reducer;

export const { updateRequestStatus } = settingsSlice.actions;

export const selectSettings = (state: RootState) => state.settings.settings;
export const settingsStatus = (state: RootState) => state.settings.status;
